/*
 * Cart drawer upsell slider — the arrows.
 *
 * A custom element rather than a bound listener because the drawer's contents
 * are replaced wholesale by the Section Rendering API: cart-drawer.js swaps
 * #CartDrawer on every add, and cart.js swaps .drawer__inner on every quantity
 * change. Anything attached with addEventListener from outside is gone after the
 * first one. connectedCallback re-runs for free on each of those renders.
 *
 * Horizontal dragging is NOT here — <drag-scroll> already does it, finds its own
 * scroller, and is mouse-only so touch keeps its native pan. This file is only
 * the two arrows and their disabled states.
 *
 * In `stack` layout the arrows are display:none and the viewport does not
 * overflow, so everything below no-ops on its own.
 */

/*
 * Where the strip was left, kept OUTSIDE the element on purpose.
 *
 * Adding from the upsell posts to /cart/add.js, and cart.js then replaces
 * .drawer__inner wholesale — so this <cart-upsell> is destroyed and a new one
 * built. Anything held on the instance dies with it, and the replacement opened
 * at scrollLeft 0. Measured: swipe to 702, tap +, land back at 0. On desktop the
 * arrows make that a shrug; on touch it means re-swiping the whole strip after
 * every single add, which is the "hassle" this file exists to avoid.
 *
 * Module scope survives the swap because the script is only evaluated once.
 */
let lastScrollLeft = 0;

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-cd-upsell-track]')?.parentElement;
    this.track = this.querySelector('[data-cd-upsell-track]');
    this.prev = this.querySelector('[data-cd-upsell-prev]');
    this.next = this.querySelector('[data-cd-upsell-next]');
    if (!this.viewport || !this.track) return;

    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    this.onScroll = () => {
      lastScrollLeft = this.viewport.scrollLeft;
      this.sync();
    };

    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    // The card width is only knowable after layout, and a late brand font can
    // reflow it, so re-measure rather than caching a number at connect.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.sync());
      this.observer.observe(this.viewport);
    }

    this.restoreScroll();
    this.sync();
  }

  /*
   * Put the strip back where it was, clamped: the product just added is filtered
   * out of the new list, so the track is one card shorter and the old offset can
   * now be past the end. Assigned directly rather than via scrollTo({behavior})
   * — this must land before the first paint of the new markup, not animate into
   * place. Reading scrollWidth first forces the layout that makes it stick.
   */
  restoreScroll() {
    if (!lastScrollLeft) return;
    const max = this.viewport.scrollWidth - this.viewport.clientWidth;
    if (max <= 0) return;
    this.viewport.scrollLeft = Math.min(lastScrollLeft, max);
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
  }

  /* One card plus the gap the flex track actually reports. */
  get stride() {
    const card = this.track.querySelector('.cd-card');
    if (!card) return this.viewport.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  step(direction) {
    this.viewport.scrollBy({
      left: this.stride * direction,
      behavior: this.reduceMotion ? 'instant' : 'smooth',
    });
  }

  get reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  sync() {
    const max = this.viewport.scrollWidth - this.viewport.clientWidth;
    // Sub-pixel: a track that fits can still report a fraction of overflow.
    const atStart = this.viewport.scrollLeft <= 1;
    const atEnd = this.viewport.scrollLeft >= max - 1;
    const overflows = max > 1;

    if (this.prev) this.prev.disabled = !overflows || atStart;
    if (this.next) this.next.disabled = !overflows || atEnd;
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
